"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import embla_carousel_autoplay from "embla-carousel-autoplay";
import embla_carousel_fade from "embla-carousel-fade";
import embla_carousel_react from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useReducedMotion } from "motion/react";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useDetectTouchDevice, useSafeInView } from "../../hooks/index.js";
import { cn } from "../../utils/cn.js";
import { Image } from "../Image/index.js";
import { carouselVariants } from "./Carousel.variants.js";
import { useCarouselAutoplayWithContext } from "./CarouselAutoplayContextHook.js";
import { useCarouselAutoplay } from "./CarouselAutoplayHook.js";
import { CarouselControls } from "./CarouselControls.js";
import { useCarouselDotButton } from "./CarouselDotButtonHooks.js";
import { useCarouselPrevNextButtons } from "./CarouselPrevNextButtonHooks.js";
function getImageSlideProps(isSelectedSlide) {
    return {
        inertProps: isSelectedSlide ? {} : {
            inert: ''
        },
        tabIndexProps: isSelectedSlide ? {
            tabIndex: 0
        } : {}
    };
}
function renderCarouselImage({ imageSrc, imageClassName, alt, objectFit = 'cover', lazyLoad = false, lazyRootMargin = '200px', lazyViewportRef, lazyForceLoad = false }) {
    return /*#__PURE__*/ jsx(Image, {
        src: imageSrc,
        className: imageClassName,
        alt: alt,
        objectFit: objectFit,
        width: "full",
        height: "full",
        objectPosition: "center",
        lazyLoad: lazyLoad,
        lazyRootMargin: lazyRootMargin,
        lazyViewportRef: lazyViewportRef,
        lazyForceLoad: lazyForceLoad
    });
}
function getCarouselConfig(props, isTouchDevice, generatedId) {
    const { type, pagination } = props;
    const isImageCarousel = 'image' === type;
    const isContentCarousel = 'content' === type;
    const shouldShowArrowBtns = 'numbers' === pagination;
    const hideArrowsOnMobileOrTouch = isTouchDevice ? 'always' : 'responsive';
    return {
        isImageCarousel,
        isContentCarousel,
        loop: isImageCarousel ? props.loop ?? true : false,
        hideArrowsOnMobileOrTouch,
        isLightBoxSupported: isImageCarousel ? props.isLightBoxSupported ?? false : false,
        autoplayDelay: isImageCarousel ? props.autoplayDelay ?? 5000 : 5000,
        syncWithGlobalAutoplay: isImageCarousel ? props.syncWithGlobalAutoplay ?? false : false,
        carouselId: props.carouselId || generatedId,
        dragFree: isContentCarousel ? props.dragFree ?? false : false,
        slidesToScrollProp: isContentCarousel ? props.slidesToScroll : void 0,
        shouldUseAutoplay: isImageCarousel ? 'hover' === props.autoplay || 'inview' === props.autoplay || 'init' === props.autoplay : false,
        showSlideNum: isImageCarousel && 'numbers' === pagination,
        showLightboxBtn: isImageCarousel ? props.isLightBoxSupported ?? false : false,
        showArrowBtns: shouldShowArrowBtns,
        showDotBtns: 'dots' === pagination,
        showAutoplayBtn: isImageCarousel ? 'hover' === props.autoplay || 'inview' === props.autoplay || 'init' === props.autoplay : false,
        showBlurBox: isImageCarousel && ('numbers' === pagination || props.isLightBoxSupported || 'hover' === props.autoplay || 'inview' === props.autoplay || 'init' === props.autoplay),
        autoplayOnHover: isImageCarousel ? 'hover' === props.autoplay : false,
        autoplayOnInView: isImageCarousel ? 'inview' === props.autoplay : false,
        autoplayOnInit: isImageCarousel ? 'init' === props.autoplay : false,
        fade: props.fade ?? false
    };
}
function renderSlide({ slide, index, slideClasses, isImageCarousel, isSelectedSlide, minSlideWidth, slideWidth, lazyLoad, lazyLoadRootMargin, viewportRef }) {
    if (isImageCarousel) {
        const imageSlide = slide;
        const alt = imageSlide.title ?? `Slide ${index + 1}`;
        const ariaLabel = imageSlide.title ?? `Slide ${index + 1}`;
        const { inertProps, tabIndexProps } = getImageSlideProps(isSelectedSlide);
        return /*#__PURE__*/ jsx("li", {
            className: slideClasses,
            "aria-hidden": !isSelectedSlide,
            ...inertProps,
            ...tabIndexProps,
            "aria-label": ariaLabel,
            children: renderCarouselImage({
                imageSrc: imageSlide.image,
                imageClassName: imageSlide.imageClassName,
                alt,
                objectFit: 'cover',
                lazyLoad,
                lazyRootMargin: lazyLoadRootMargin,
                lazyViewportRef: viewportRef,
                lazyForceLoad: isSelectedSlide
            })
        }, slide.id);
    }
    const contentSlide = slide;
    const customMinWidthStyle = 'min' === slideWidth && minSlideWidth ? {
        minWidth: minSlideWidth
    } : {};
    return /*#__PURE__*/ jsx("li", {
        className: slideClasses,
        style: customMinWidthStyle,
        "aria-label": contentSlide.title ?? `Slide ${index + 1}`,
        children: contentSlide.childrenNodes
    }, slide.id);
}
function Carousel(props) {
    const { slotClassNames = {}, slides, ariaLabel, ...restProps } = props;
    const lazyLoad = 'lazyLoad' in props ? props.lazyLoad ?? false : false;
    const lazyLoadRootMargin = 'lazyLoadRootMargin' in props ? props.lazyLoadRootMargin ?? '200px' : '200px';
    const generatedId = useId();
    const isTouchDevice = useDetectTouchDevice();
    const prefersReducedMotion = useReducedMotion() ?? false;
    const config = getCarouselConfig(props, isTouchDevice, generatedId);
    const minSlideWidth = 'minSlideWidth' in props ? props.minSlideWidth : void 0;
    const slideWidth = props.slideWidth ?? ('content' === props.type ? 'auto' : 'image');
    const emblaOptions = useMemo(()=>{
        const options = {
            loop: config.loop
        };
        if (config.dragFree) options.dragFree = true;
        if (void 0 !== config.slidesToScrollProp) options.slidesToScroll = config.slidesToScrollProp;
        return options;
    }, [
        config.loop,
        config.dragFree,
        config.slidesToScrollProp
    ]);
    const plugins = useMemo(()=>{
        const basePlugins = [
            WheelGesturesPlugin()
        ];
        if (config.fade) basePlugins.push(embla_carousel_fade());
        if (config.shouldUseAutoplay) basePlugins.push(embla_carousel_autoplay({
            playOnInit: !prefersReducedMotion && !config.autoplayOnHover && !config.autoplayOnInView && config.autoplayOnInit,
            delay: config.autoplayDelay,
            stopOnMouseEnter: false,
            stopOnFocusIn: false,
            stopOnInteraction: false
        }));
        return basePlugins;
    }, [
        config.shouldUseAutoplay,
        config.fade,
        config.autoplayOnHover,
        config.autoplayOnInView,
        config.autoplayOnInit,
        config.autoplayDelay,
        prefersReducedMotion
    ]);
    const [emblaRef, emblaApi] = embla_carousel_react(emblaOptions, plugins);
    const viewportRef = useRef(null);
    const emblaCallbackRef = useCallback((node)=>{
        viewportRef.current = node;
        emblaRef(node);
    }, [
        emblaRef
    ]);
    const { selectedIndex, onDotButtonClick } = useCarouselDotButton(emblaApi);
    const { prevBtnDisabled, nextBtnDisabled, prevBtnHidden, onPrevButtonClick, onNextButtonClick } = useCarouselPrevNextButtons(emblaApi, config.loop);
    const contextAutoplayOptions = {
        syncWithGlobalAutoplay: config.syncWithGlobalAutoplay,
        carouselId: config.carouselId
    };
    const standardAutoplayResult = useCarouselAutoplay(config.shouldUseAutoplay && !config.syncWithGlobalAutoplay ? emblaApi : void 0);
    const contextAutoplayResult = useCarouselAutoplayWithContext(config.shouldUseAutoplay && config.syncWithGlobalAutoplay ? emblaApi : void 0, contextAutoplayOptions);
    let autoplayResult;
    autoplayResult = config.syncWithGlobalAutoplay ? contextAutoplayResult : config.shouldUseAutoplay ? standardAutoplayResult : null;
    const { autoplayIsPlaying = false, autoplayIsManuallyPaused = false, toggleAutoplay = ()=>{}, onAutoplayButtonClick = (callback)=>callback() } = autoplayResult || {};
    const onLightboxButtonClick = (callback)=>callback();
    const handleMouseEnter = ()=>{
        if (config.autoplayOnHover && !autoplayIsManuallyPaused) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay) {
                if (!prefersReducedMotion || autoplay.isPlaying()) autoplay.play();
            }
        }
    };
    const handleCarouselPointerDown = (event)=>{
        if (!config.shouldUseAutoplay) return;
        const target = event.target;
        const isAutoplayButton = target.closest('[data-testid="carousel-autoplay-button"]') || 'carousel-autoplay-button' === target.dataset.testid;
        if (!isAutoplayButton) {
            event.preventDefault();
            event.stopPropagation();
        }
    };
    const handleMouseLeave = ()=>{
        if (config.autoplayOnHover) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay) {
                if (prefersReducedMotion && autoplay.isPlaying()) return;
                autoplay.stop();
            }
        }
    };
    const autoplayInViewRef = useRef(null);
    const isInView = useSafeInView(autoplayInViewRef, {
        once: false
    });
    useEffect(()=>{
        if (config.autoplayOnInView) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (!autoplay) return;
            if (prefersReducedMotion) autoplay.stop();
            else if (isInView && !autoplayIsManuallyPaused) autoplay.play();
            else autoplay.stop();
        }
    }, [
        emblaApi,
        isInView,
        config.autoplayOnInView,
        autoplayIsManuallyPaused,
        prefersReducedMotion
    ]);
    useEffect(()=>{
        if (prefersReducedMotion) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay && autoplay.isPlaying()) autoplay.stop();
        }
    }, [
        emblaApi,
        prefersReducedMotion
    ]);
    const { wrapperParentClass = '', wrapperClass = '', rootClass = '', viewportClass = '', containerClass = '', slideClass = '', buttonClass = '', dotsPaginationClass = '', dotButtonClass = '', dotClass = '', blurBoxClass = '', blurBoxDividerClass = '', blurBoxNumbersClass = '', blurBoxLightboxButtonClass = '', blurBoxAutoplayButtonClass = '' } = slotClassNames;
    const { wrapperParentSlot, wrapperSlot, rootSlot, viewportSlot, containerSlot, prevButtonSlot, nextButtonSlot, hiddenButtonSlot, dotsPaginationSlot, dotButtonSlot, slideSlot } = carouselVariants({
        ...restProps,
        type: config.isImageCarousel ? 'image' : 'content',
        loop: config.loop,
        hideArrowsOnMobileOrTouch: config.hideArrowsOnMobileOrTouch,
        bleedSpacing: restProps.bleedSpacing
    });
    const wrapperParentClasses = cn(wrapperParentSlot(), wrapperParentClass);
    const wrapperClasses = cn(wrapperSlot(), wrapperClass);
    const rootClasses = cn(rootSlot(), rootClass);
    const viewportClasses = cn(viewportSlot(), viewportClass);
    const containerClasses = cn(containerSlot(), containerClass);
    const slideClasses = cn(slideSlot(), slideClass);
    const currentSlide = selectedIndex + 1;
    const totalSlides = slides?.length ?? 0;
    const onPrevNextButtonClick = useCallback((callback)=>{
        if (config.shouldUseAutoplay) onAutoplayButtonClick(callback);
        else callback();
    }, [
        config.shouldUseAutoplay,
        onAutoplayButtonClick
    ]);
    return /*#__PURE__*/ jsx("div", {
        "data-testid": "carousel-wrapper-parent",
        className: wrapperParentClasses,
        children: /*#__PURE__*/ jsx("div", {
            "data-testid": "carousel-wrapper",
            className: wrapperClasses,
            children: /*#__PURE__*/ jsxs("section", {
                "aria-label": ariaLabel,
                "data-testid": "carousel-root",
                className: rootClasses,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                onPointerDown: handleCarouselPointerDown,
                ref: autoplayInViewRef,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        "data-testid": "carousel-viewport",
                        className: viewportClasses,
                        ref: emblaCallbackRef,
                        children: /*#__PURE__*/ jsx("ul", {
                            "data-testid": "carousel-container",
                            className: containerClasses,
                            children: slides?.map((slide, index)=>{
                                const isSelectedSlide = index === selectedIndex;
                                return renderSlide({
                                    slide,
                                    index,
                                    slideClasses,
                                    isImageCarousel: config.isImageCarousel,
                                    isSelectedSlide,
                                    minSlideWidth,
                                    slideWidth,
                                    lazyLoad,
                                    lazyLoadRootMargin,
                                    viewportRef
                                });
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx(CarouselControls, {
                        config: config,
                        slides: slides,
                        selectedIndex: selectedIndex,
                        prevBtnDisabled: prevBtnDisabled,
                        nextBtnDisabled: nextBtnDisabled,
                        prevBtnHidden: prevBtnHidden,
                        onPrevButtonClick: onPrevButtonClick,
                        onNextButtonClick: onNextButtonClick,
                        onDotButtonClick: onDotButtonClick,
                        onPrevNextButtonClick: onPrevNextButtonClick,
                        onAutoplayButtonClick: onAutoplayButtonClick,
                        onLightboxButtonClick: onLightboxButtonClick,
                        toggleAutoplay: toggleAutoplay,
                        autoplayIsPlaying: autoplayIsPlaying,
                        slotClassNames: {
                            buttonClass,
                            dotsPaginationClass,
                            dotButtonClass,
                            dotClass,
                            blurBoxClass,
                            blurBoxDividerClass,
                            blurBoxNumbersClass,
                            blurBoxLightboxButtonClass,
                            blurBoxAutoplayButtonClass
                        },
                        variantSlots: {
                            prevButtonSlot,
                            nextButtonSlot,
                            hiddenButtonSlot,
                            dotsPaginationSlot,
                            dotButtonSlot
                        },
                        currentSlide: currentSlide,
                        totalSlides: totalSlides
                    })
                ]
            })
        })
    });
}
const Carousel_Carousel = Carousel;
export { Carousel, Carousel_Carousel as default };
